import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import API from "../../utils/api";
import { useAuth } from "../../App";
import TemplatePicker, { applyVariables } from "../../components/TemplatePicker";
import { celebrate } from "../../components/CelebrationHub";
import { uploadPhoto } from "../../utils/offlineVisits";

//  Constants 

const STATUS_TABS = [
  { key: "all",       label: "All",      icon: "" },
  { key: "scheduled", label: "Upcoming", icon: "" },
  { key: "missed",    label: "Missed",   icon: "" },
  { key: "completed", label: "Done",     icon: "" },
];

const ACTION_LABELS = {
  send_proposal:    "Send Proposal",
  meet_management:  "Meet Management",
  asked_to_revisit: "Asked to Revisit",
  other:            "Other",
};
const INTEREST_LABELS = {
  ebek_lpi:    "EBEK LPI",
  bookr:       "Bookr",
  ceq:         "CEQ",
  linguaskill: "Linguaskill",
  others:      "Others",
  inc_mandatory_modules: "INC mandatory Modules",
  osce_solutions: "OSCE Solutions",
  online_competency_modules: "Online Competency modules",
  hands_on_training: "Hands-On Training",
  competency_management_solutions: "Competency Management solutions",
};
const ACTION_OPTIONS = [
  { value: "send_proposal",    label: "Send Proposal"    },
  { value: "meet_management",  label: "Meet Management"  },
  { value: "asked_to_revisit", label: "Asked to Revisit" },
  { value: "other",            label: "Other"            },
];
const INTEREST_OPTIONS = [
  { value: "ebek_lpi",     label: "EBEK LPI"    },
  { value: "bookr",        label: "Bookr"       },
  { value: "ceq",          label: "CEQ"         },
  { value: "linguaskill",  label: "Linguaskill" },
  { value: "others",       label: "Others"      },
];
const EBEKMED_INTEREST_OPTIONS = [
  { value: "inc_mandatory_modules", label: "INC mandatory Modules" },
  { value: "osce_solutions", label: "OSCE Solutions" },
  { value: "online_competency_modules", label: "Online Competency modules" },
  { value: "hands_on_training", label: "Hands-On Training" },
  { value: "competency_management_solutions", label: "Competency Management solutions" },
  { value: "others", label: "Others" },
];
const TEMPERATURE_OPTIONS = [
  { value: "hot",            label: " Hot",           color: "#dc2626", bg: "#fee2e2" },
  { value: "warm",           label: " Warm",          color: "#b45309", bg: "#fef9c3" },
  { value: "cold",           label: " Cold",          color: "#1d4ed8", bg: "#dbeafe" },
  { value: "not_interested", label: " Not Interested", color: "#fff",   bg: "#1e293b" },
];
const PIPELINE_ADVANCE_OPTIONS = [
  { value: "interested",        label: "Interested"         },
  { value: "proposal_sent",     label: "Proposal Sent"      },
  { value: "mou_shared",        label: "MOU Shared"         },
  { value: "mou_signed",        label: "MOU Signed"         },
  { value: "payment_requested", label: "Payment Requested"  },
  { value: "closed_won",        label: "Payment Received " },
];

const STAGE_LABELS = {
  visited: "Visited", interested: "Interested", proposal_sent: "Proposal Sent",
  mou_shared: "MOU Shared", mou_signed: "MOU Signed",
  payment_requested: "Payment Req.", closed_won: "Closed Won", closed_lost: "Closed Lost",
};
const STAGE_COLORS = {
  visited: "#94a3b8", interested: "#3B82F6", proposal_sent: "#d97706",
  mou_shared: "#7c3aed", mou_signed: "#0ea5e9", payment_requested: "#f59e0b",
  closed_won: "#16a34a", closed_lost: "#dc2626",
};

const QUICK_ACTION_TYPES = [
  { value: "email",       label: "Email",       icon: "", color: "#0ea5e9", bg: "#e0f2fe" },
  { value: "phone",       label: "Phone",       icon: "", color: "#16a34a", bg: "#dcfce7" },
  { value: "whatsapp",    label: "WhatsApp",    icon: "", color: "#166534", bg: "#dcfce7" },
  { value: "direct_meet", label: "Direct Meeting", icon: "", color: "#7c3aed", bg: "#f5f3ff" },
];

const QUICK_CALL_OUTCOMES = [
  { key: "call_picked", label: "Call Picked" },
  { key: "ringing_not_picked", label: "Ringing Not Picked" },
  { key: "disconnected", label: "Disconnected the call" },
  { key: "not_reachable", label: "Not reachable" },
  { key: "call_back_later", label: "Call back Later" },
  { key: "not_interested_no_more_calls", label: "Not Interested / No more calls" },
];

const QUICK_EMAIL_STATUSES = [
  { key: "sent_proposal", label: "Sent Proposal" },
  { key: "sent_mou", label: "Sent MOU" },
  { key: "sent_follow_up", label: "Sent Follow-Up" },
  { key: "others", label: "Others" },
];

//  Helpers 

function formatDate(d) {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function formatDateTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
function isOverdue(f) {
  if (f.status !== "scheduled") return false;
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  return f.scheduled_date < today;
}
function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

//  Quick Actions Panel 

function QuickActionsPanel({ accent }) {
  const { user } = useAuth();
  const [institutions, setInstitutions] = useState([]);
  const [recentActions, setRecentActions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");

  // Modal state
  const [actionModal, setActionModal]       = useState(null); // { inst, type }
  const [notes, setNotes]                   = useState("");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false); // "email"|"whatsapp"|false
  const [quickCallOutcome, setQuickCallOutcome] = useState("call_picked");
  const [quickEmailStatus, setQuickEmailStatus] = useState("sent_follow_up");
  const [quickEmailOther, setQuickEmailOther] = useState("");
  const [gpsLoading, setGpsLoading]         = useState(false);
  const [gpsPos, setGpsPos]                 = useState(null);
  const [gpsError, setGpsError]             = useState("");
  const [submitting, setSubmitting]         = useState(false);

  // Direct meeting evidence: a photo of the institution and who was actually met
  const [photoFile, setPhotoFile]           = useState(null);
  const [photoPreview, setPhotoPreview]     = useState(null);
  const [metSame, setMetSame]               = useState(true);
  const [metName, setMetName]               = useState("");
  const [metPhone, setMetPhone]             = useState("");
  const [metDesignation, setMetDesignation] = useState("");

  // Voice memo recording state
  const [recording, setRecording]   = useState(false);
  const [audioBlob, setAudioBlob]   = useState(null);
  const [audioUrl, setAudioUrl]     = useState(null);
  const [recSeconds, setRecSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const recTimerRef      = useRef(null);

  // Load institutions + recent actions
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [instRes, actRes] = await Promise.all([
        API.get("/follow-up-actions/institutions"),
        API.get("/follow-up-actions/my"),
      ]);
      setInstitutions(instRes.data);
      setRecentActions(actRes.data);
    } catch {
      toast.error("Could not load institutions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = institutions.filter(i =>
    !search || i.institution_name.toLowerCase().includes(search.toLowerCase())
  );

  // ── Voice memo recording helpers ─────────────────────────────
  const _resetRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    clearInterval(recTimerRef.current);
    setRecording(false);
    setAudioBlob(null);
    setAudioUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setRecSeconds(0);
    chunksRef.current = [];
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a supported mimeType — Safari needs mp4, Chrome/Android prefer webm
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
          ? "audio/mp4"
          : "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop()); // release microphone
      };
      mr.start(250); // collect data every 250ms
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => setRecSeconds(s => s + 1), 1000);
    } catch (err) {
      toast.error("Microphone access denied — please allow mic permission and try again.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    clearInterval(recTimerRef.current);
    setRecording(false);
  };

  const clearRecording = () => {
    _resetRecording();
  };

  const resetMeetDetails = () => {
    setPhotoFile(null);
    setPhotoPreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setMetSame(true);
    setMetName("");
    setMetPhone("");
    setMetDesignation("");
  };

  const pickPhoto = (file) => {
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(file); });
  };

  // The details are valid when nothing is needed (same contact) or a name and a
  // reachable phone number were entered for whoever was met instead.
  const metContactValid = metSame || (metName.trim().length > 0 && metPhone.replace(/\D/g, "").length >= 7);
  const meetBlocked = actionModal?.type === "direct_meet" && (!gpsPos || !photoFile || !metContactValid);

  // Open action modal
  const openAction = (inst, type) => {
    _resetRecording(); // clear any stale recording from previous modal
    resetMeetDetails();
    setActionModal({ inst, type });
    setNotes("");
    setQuickCallOutcome("call_picked");
    setQuickEmailStatus("sent_follow_up");
    setQuickEmailOther("");
    setGpsPos(null);
    setGpsError("");
    // Auto-capture GPS for direct meet
    if (type === "direct_meet") {
      captureGPS();
    }
  };
  const closeAction = () => {
    _resetRecording();
    resetMeetDetails();
    setActionModal(null);
    setGpsPos(null);
    setGpsError("");
    setNotes("");
  };

  // Capture GPS
  const captureGPS = () => {
    setGpsLoading(true);
    setGpsError("");
    if (!navigator.geolocation) {
      setGpsError("Geolocation not supported on this device");
      setGpsLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsLoading(false);
      },
      (err) => {
        setGpsError("Could not get location. Please enable GPS and try again.");
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Submit follow-up action
  const handleSubmit = async () => {
    if (!actionModal) return;
    const { inst, type } = actionModal;

    if (type === "direct_meet" && !gpsPos) {
      return toast.error("Location required for Direct Meeting. Please allow GPS access.");
    }
    if (type === "direct_meet" && !photoFile) {
      return toast.error("Take a photo of the institution for the Direct Meeting.");
    }
    if (type === "direct_meet" && !metContactValid) {
      return toast.error("Enter the name and phone number of the person you met.");
    }

    setSubmitting(true);
    try {
      let photoUrl = null;
      if (type === "direct_meet") {
        try {
          photoUrl = await uploadPhoto(photoFile);
        } catch (err) {
          setSubmitting(false);
          return toast.error(err?.message || "Could not upload the photo. Check your connection and try again.");
        }
      }

      // If there's a voice memo, upload it first and get the URL
      let voiceMemoUrl = null;
      if (audioBlob) {
        const fd = new FormData();
        fd.append("audio", audioBlob, "memo.webm");
        const { data } = await API.post("/follow-up-actions/voice-memo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        voiceMemoUrl = data.url;
      }

      const body = {
        lead_id:           inst.lead_id || null,
        institution_name:  inst.institution_name,
        contact_name:      inst.contact_name  || null,
        contact_phone:     inst.contact_phone || null,
        contact_email:     inst.contact_email || null,
        action_type:       type,
        notes:             [
          type === "phone" ? `Call status: ${QUICK_CALL_OUTCOMES.find(item => item.key === quickCallOutcome)?.label || quickCallOutcome}` : "",
          type === "email" ? `Email status: ${quickEmailStatus === "others" ? quickEmailOther.trim() : QUICK_EMAIL_STATUSES.find(item => item.key === quickEmailStatus)?.label || quickEmailStatus}` : "",
          notes || "",
        ].filter(Boolean).join("\n") || null,
        latitude:          gpsPos ? gpsPos.lat : null,
        longitude:         gpsPos ? gpsPos.lng : null,
        expected_latitude:  inst.latitude  || null,
        expected_longitude: inst.longitude || null,
        voice_memo_url:    voiceMemoUrl,
        ...(type === "direct_meet" ? {
          photo_url:               photoUrl,
          met_same_contact:        metSame,
          met_contact_name:        metSame ? null : metName.trim(),
          met_contact_phone:       metSame ? null : metPhone.trim(),
          met_contact_designation: metSame ? null : (metDesignation.trim() || null),
        } : null),
      };

      const res = await API.post("/follow-up-actions", body);

      // For direct meet: silently note mismatch but don't alert the employee
      if (type === "direct_meet") {
        toast.success("Direct meet logged successfully ");
      } else if (type === "email") {
        toast.success("Email follow-up logged ");
      } else if (type === "whatsapp") {
        toast.success("WhatsApp follow-up logged ");
      } else {
        toast.success("Phone call logged ");
      }

      closeAction();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to log action");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle email/phone/whatsapp shortcuts
  const handleEmailShortcut = (inst) => {
    // Open the CRM email compose modal — never use mailto: which causes blank screen on mobile PWA
    openAction(inst, "email");
  };
  const handlePhoneShortcut = (inst) => {
    if (inst.contact_phone) {
      // Use a hidden anchor click — most reliable way to trigger the phone dialer in a PWA
      const a = document.createElement("a");
      a.href = `tel:${inst.contact_phone}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    // Open the call-log modal after a short delay so the dialer can launch first
    setTimeout(() => openAction(inst, "phone"), 800);
  };
  const handleWhatsAppShortcut = (inst) => {
    if (!inst.contact_phone) return toast.error("No phone number for this institution");
    const clean = inst.contact_phone.replace(/\D/g, "");
    const to    = clean.startsWith("91") ? clean : `91${clean}`;
    const greeting = encodeURIComponent(`Hi ${inst.contact_name ? inst.contact_name.split(" ")[0] : ""},`);
    window.open(`https://wa.me/${to}?text=${greeting}`, "_blank");
    openAction(inst, "whatsapp");
  };

  const inputStyle = {
    width: "100%", padding: "11px 14px", borderRadius: 12,
    border: "1.5px solid #e2e8f0", fontSize: 13,
    fontFamily: "inherit", outline: "none", background: "#fff", boxSizing: "border-box"
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6,
    display: "block", textTransform: "uppercase", letterSpacing: 0.5
  };

  const tempInfo = inst => {
    const t = inst.temperature;
    if (t === "hot")  return { icon: "", color: "#dc2626" };
    if (t === "warm") return { icon: "", color: "#b45309" };
    if (t === "cold") return { icon: "", color: "#1d4ed8" };
    return { icon: "", color: "#94a3b8" };
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>Loading</div>;

  return (
    <div>
      {/* Search */}
      <div style={{ padding: "12px 16px 0" }}>
        <input
          type="text"
          placeholder=" Search institution"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, background: "#f8fafc", border: "1.5px solid #e2e8f0" }}
        />
      </div>

      {/* Institutions list */}
      <div style={{ padding: "12px 16px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
          Your Institutions ({filtered.length})
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}></div>
            <div style={{ fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>No institutions found</div>
            <div style={{ fontSize: 13, color: "#94a3b8" }}>Add leads to see institutions here</div>
          </div>
        ) : filtered.map(inst => {
          const ti = tempInfo(inst);
          return (
            <div key={inst.lead_id || inst.institution_name}
              style={{ background: "#fff", borderRadius: 16, padding: 14, marginBottom: 10, border: "1px solid #e2e8f0" }}>
              {/* Name + stage */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: "#1e293b", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {ti.icon} {inst.institution_name}
                  </div>
                  {inst.contact_name && (
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}> {inst.contact_name}</div>
                  )}
                </div>
                {inst.pipeline_stage && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, flexShrink: 0, marginLeft: 8,
                    background: (STAGE_COLORS[inst.pipeline_stage] || "#94a3b8") + "20",
                    color: STAGE_COLORS[inst.pipeline_stage] || "#94a3b8"
                  }}>
                    {STAGE_LABELS[inst.pipeline_stage] || inst.pipeline_stage}
                  </span>
                )}
              </div>

              {/* Contact info */}
              {(inst.contact_phone || inst.contact_email) && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {inst.contact_phone && (
                    <span style={{ fontSize: 11, color: "#64748b" }}> {inst.contact_phone}</span>
                  )}
                  {inst.contact_email && (
                    <span style={{ fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis" }}> {inst.contact_email}</span>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 6 }}>
                {/* Email */}
                <button onClick={() => handleEmailShortcut(inst)}
                  style={{ flex: 1, padding: "9px 4px", background: "#e0f2fe", color: "#0369a1", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                   Email
                </button>
                {/* WhatsApp */}
                {inst.contact_phone && (
                  <button onClick={() => handleWhatsAppShortcut(inst)}
                    style={{ flex: 1, padding: "9px 4px", background: "#dcfce7", color: "#166534", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    💬 WA
                  </button>
                )}
                {/* Phone */}
                <button onClick={() => handlePhoneShortcut(inst)}
                  style={{ flex: 1, padding: "9px 4px", background: "#f0fdf4", color: "#15803d", border: "1.5px solid #bbf7d0", borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                   Call
                </button>
                {/* Direct Meeting */}
                <button onClick={() => openAction(inst, "direct_meet")}
                  style={{ flex: 1, padding: "9px 4px", background: "#f5f3ff", color: "#7c3aed", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                   Meet
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Actions */}
      {recentActions.length > 0 && (
        <div style={{ padding: "0 16px 80px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
            Recent Actions
          </div>
          {recentActions.slice(0, 10).map(a => {
            const at = QUICK_ACTION_TYPES.find(t => t.value === a.action_type) || { icon: "", color: "#64748b", bg: "#f1f5f9" };
            return (
              <div key={a.id} style={{ background: "#fff", borderRadius: 14, padding: "12px 14px", marginBottom: 8, border: "1px solid #f1f5f9", display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: at.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                  {at.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.institution_name}
                  </div>
                  <div style={{ fontSize: 11, color: at.color, fontWeight: 600, marginTop: 1 }}>
                    {at.label || a.action_type}
                    {a.contact_name && <span style={{ color: "#94a3b8", fontWeight: 400 }}>  {a.contact_name}</span>}
                  </div>
                  {a.notes && (
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.notes}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>{formatDateTime(a.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/*  Action Log Modal  */}
      {actionModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 17, color: "#1e293b" }}>
                  {QUICK_ACTION_TYPES.find(t => t.value === actionModal.type)?.icon} Log {QUICK_ACTION_TYPES.find(t => t.value === actionModal.type)?.label}
                </div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>
                  {actionModal.inst.institution_name}
                </div>
              </div>
              <button onClick={closeAction}
                style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}></button>
            </div>

            {/* Contact info display */}
            {(actionModal.inst.contact_name || actionModal.inst.contact_phone) && (
              <div style={{ background: "#f8fafc", borderRadius: 12, padding: 12, marginBottom: 16 }}>
                {actionModal.inst.contact_name  && <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 600 }}> {actionModal.inst.contact_name}</div>}
                {actionModal.inst.contact_phone && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}> {actionModal.inst.contact_phone}</div>}
                {actionModal.inst.contact_email && <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}> {actionModal.inst.contact_email}</div>}
              </div>
            )}

            {/* GPS section  only for direct_meet */}
            {actionModal.type === "direct_meet" && (
              <div style={{ marginBottom: 16, background: "#faf5ff", borderRadius: 14, padding: 14, border: "1.5px solid #e9d5ff" }}>
                <label style={{ ...labelStyle, color: "#7c3aed" }}> Your Location (Required for Direct Meeting)</label>

                {gpsLoading && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#7c3aed", fontSize: 13 }}>
                    <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}></span>
                    Getting your location
                  </div>
                )}

                {!gpsLoading && gpsPos && (
                  <div style={{ background: "#f0fdf4", borderRadius: 10, padding: 10, border: "1px solid #86efac" }}>
                    <div style={{ fontSize: 12, color: "#15803d", fontWeight: 700 }}> Location captured</div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                      {gpsPos.lat.toFixed(6)}, {gpsPos.lng.toFixed(6)}
                    </div>
                    {actionModal.inst.latitude && actionModal.inst.longitude && (
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                         ~{haversineMeters(gpsPos.lat, gpsPos.lng, parseFloat(actionModal.inst.latitude), parseFloat(actionModal.inst.longitude))}m from institution
                      </div>
                    )}
                  </div>
                )}

                {!gpsLoading && !gpsPos && (
                  <div>
                    {gpsError && (
                      <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 8 }}> {gpsError}</div>
                    )}
                    <button onClick={captureGPS}
                      style={{ padding: "10px 20px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                       Capture My Location
                    </button>
                  </div>
                )}

                {!gpsLoading && gpsPos && (
                  <button onClick={captureGPS} style={{ marginTop: 8, padding: "7px 14px", background: "none", color: "#7c3aed", border: "1px solid #e9d5ff", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                     Refresh Location
                  </button>
                )}
              </div>
            )}

            {/* Institution photo and who was met — direct meetings only */}
            {actionModal.type === "direct_meet" && (
              <>
                <div style={{ marginBottom: 16, background: "#faf5ff", borderRadius: 14, padding: 14, border: "1.5px solid #e9d5ff" }}>
                  <label style={{ ...labelStyle, color: "#7c3aed" }}>Institution Photo (Required)</label>
                  {photoPreview && (
                    <img src={photoPreview} alt="Institution"
                      style={{ display: "block", width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 10, marginBottom: 8 }} />
                  )}
                  <label style={{ display: "inline-block", padding: "10px 20px", background: "#7c3aed", color: "#fff", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    {photoFile ? "Retake Photo" : "Take Photo"}
                    <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                      onChange={e => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                </div>

                <div style={{ marginBottom: 16, background: "#faf5ff", borderRadius: 14, padding: 14, border: "1.5px solid #e9d5ff" }}>
                  <label style={{ ...labelStyle, color: "#7c3aed" }}>Whom did you meet?</label>
                  {(() => {
                    const inst = actionModal.inst;
                    const onFile = [inst.contact_name, inst.contact_phone].filter(Boolean).join(" · ");
                    const options = [
                      { same: true, label: onFile ? `Same person: ${onFile}` : "Same contact as on file" },
                      { same: false, label: "Someone else" },
                    ];
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {options.map(opt => {
                          const sel = metSame === opt.same;
                          return (
                            <button key={String(opt.same)} type="button" onClick={() => setMetSame(opt.same)}
                              style={{ textAlign: "left", padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${sel ? "#7c3aed" : "#e2e8f0"}`, background: sel ? "#ede9fe" : "#fff", color: sel ? "#6d28d9" : "#64748b", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {!metSame && (
                    <div style={{ marginTop: 10 }}>
                      {(actionModal.inst.contacts || []).length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 5 }}>Already recorded here — tap to use:</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {actionModal.inst.contacts.map((c, idx) => (
                              <button key={idx} type="button"
                                onClick={() => { setMetName(c.name || ""); setMetPhone(c.phone || ""); setMetDesignation(c.designation || ""); }}
                                style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid #ddd6fe", background: "#fff", color: "#6d28d9", fontWeight: 700, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                                {c.name}{c.designation ? ` (${c.designation})` : ""}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <input value={metName} onChange={e => setMetName(e.target.value)} placeholder="Name of the person you met *"
                        style={{ ...inputStyle, marginBottom: 8 }} />
                      <input type="tel" value={metPhone} onChange={e => setMetPhone(e.target.value)} placeholder="Phone number *"
                        style={{ ...inputStyle, marginBottom: 8 }} />
                      <input value={metDesignation} onChange={e => setMetDesignation(e.target.value)} placeholder="Designation (optional)"
                        style={inputStyle} />
                    </div>
                  )}
                </div>
              </>
            )}

            {actionModal.type === "phone" && (
              <>
                {/* Call status chips */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Call Status</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                    {QUICK_CALL_OUTCOMES.map(opt => {
                      const sel = quickCallOutcome === opt.key;
                      return (
                        <button key={opt.key} onClick={() => setQuickCallOutcome(opt.key)}
                          style={{ padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${sel ? "#15803d" : "#e2e8f0"}`, background: sel ? "#dcfce7" : "#fff", color: sel ? "#15803d" : "#64748b", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Voice Memo Recorder */}
                <div style={{ marginBottom: 16, background: "#f0fdf4", borderRadius: 14, padding: 14, border: "1.5px solid #bbf7d0" }}>
                  <label style={{ ...labelStyle, color: "#15803d" }}>🎙️ Voice Memo (Optional)</label>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
                    Record a quick note after the call — saved to the lead's activity log.
                  </div>

                  {/* Idle — no recording yet */}
                  {!audioBlob && !recording && (
                    <button onClick={startRecording}
                      style={{ padding: "10px 20px", background: "#15803d", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8 }}>
                      🎙️ Start Recording
                    </button>
                  )}

                  {/* Active recording */}
                  {recording && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#dc2626" }}>
                        <span style={{
                          width: 10, height: 10, borderRadius: "50%", background: "#dc2626",
                          display: "inline-block",
                          animation: "voicePulse 1s ease-in-out infinite"
                        }} />
                        {String(Math.floor(recSeconds / 60)).padStart(2, "0")}:{String(recSeconds % 60).padStart(2, "0")}
                      </span>
                      <button onClick={stopRecording}
                        style={{ padding: "9px 18px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                        ⏹ Stop
                      </button>
                    </div>
                  )}

                  {/* Recorded — show playback */}
                  {audioUrl && !recording && (
                    <div>
                      <audio controls src={audioUrl} style={{ width: "100%", marginBottom: 8, borderRadius: 8 }} />
                      <button onClick={clearRecording}
                        style={{ padding: "7px 14px", background: "none", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                        🗑️ Discard &amp; Re-record
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {actionModal.type === "email" && (
              <div style={{ marginBottom: 16 }}>
                {/* Template picker button */}
                <button onClick={() => setShowTemplatePicker("email")}
                  style={{ marginBottom: 10, padding: "8px 14px", background: "#e0f2fe",
                    border: "none", borderRadius: 10, fontSize: 12, fontWeight: 700,
                    cursor: "pointer", fontFamily: "inherit", color: "#0369a1" }}>
                  📝 Use Email Template
                </button>
                <label style={labelStyle}>Email Status</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                  {QUICK_EMAIL_STATUSES.map(opt => {
                    const sel = quickEmailStatus === opt.key;
                    return (
                      <button key={opt.key} onClick={() => setQuickEmailStatus(opt.key)}
                        style={{ padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${sel ? "#0369a1" : "#e2e8f0"}`, background: sel ? "#e0f2fe" : "#fff", color: sel ? "#0369a1" : "#64748b", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {quickEmailStatus === "others" && (
                  <input value={quickEmailOther} onChange={e => setQuickEmailOther(e.target.value)}
                    placeholder="Type email status"
                    style={{ ...inputStyle, marginTop: 8 }} />
                )}
              </div>
            )}

            {/* WhatsApp template button */}
            {actionModal.type === "whatsapp" && (
              <div style={{ marginBottom: 12 }}>
                <button onClick={() => setShowTemplatePicker("whatsapp")}
                  style={{ padding: "8px 14px", background: "#dcfce7",
                    border: "none", borderRadius: 10, fontSize: 12, fontWeight: 700,
                    cursor: "pointer", fontFamily: "inherit", color: "#166534" }}>
                  💬 Use WhatsApp Template
                </button>
              </div>
            )}

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Notes / Outcome</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={
                  actionModal.type === "email"       ? "What did you email about? Any response?" :
                  actionModal.type === "phone"       ? "What was discussed on the call?" :
                  "What was discussed during the visit?"
                }
                style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
              />
            </div>

            {/* Submit */}
            <div style={{ position: "sticky", bottom: -24, background: "#fff", paddingTop: 12, display: "flex", gap: 10 }}>
              <button onClick={closeAction}
                style={{ flex: 1, padding: 15, background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={submitting || meetBlocked}
                style={{
                  flex: 2, padding: 15,
                  background: actionModal.type === "direct_meet" ? "#7c3aed" : actionModal.type === "email" ? "#0369a1" : "#15803d",
                  color: "#fff", border: "none", borderRadius: 14,
                  fontSize: 15, fontWeight: 700, cursor: (submitting || meetBlocked) ? "not-allowed" : "pointer",
                  fontFamily: "inherit", opacity: (submitting || meetBlocked) ? 0.6 : 1
                }}>
                {submitting ? "Logging" :
                  actionModal.type === "email"       ? "Log Email" :
                  actionModal.type === "phone"       ? "Log Call" :
                  actionModal.type === "whatsapp"    ? "Log WhatsApp" :
                  "Log Meeting"}
              </button>
            </div>

            {meetBlocked && !gpsLoading && (
              <div style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
                {!gpsPos ? "Location capture is required before submitting a Direct Meeting"
                  : !photoFile ? "Take a photo of the institution to log the meeting"
                  : "Enter the name and phone number of the person you met"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Picker overlay */}
      {showTemplatePicker && (
        <TemplatePicker
          type={showTemplatePicker}
          accent={accent}
          vars={{
            contact_name:      actionModal?.contact_name || "",
            institution_name:  actionModal?.institution_name || "",
            sender_name:       user?.name || "",
            company_name:      user?.company === "ebekmed" ? "EbekMed" : "Ebek",
          }}
          onSelect={({ body }) => {
            setNotes(prev => prev ? prev + "\n\n" + body : body);
            toast.success("Template applied to notes");
          }}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}
    </div>
  );
}

//  Main FollowUps Component 

export default function FollowUps({ accent }) {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState("followups"); // "followups" | "actions"
  const [tab, setTab]             = useState("all");
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [modal, setModal]         = useState(null); // "complete" | "missed"
  const [submitting, setSubmitting] = useState(false);

  // Mark Done form state
  const [feedback, setFeedback]             = useState("");
  const [temperature, setTemperature]       = useState("");
  const [pipelineStage, setPipelineStage]   = useState("");
  const [interestedIn, setInterestedIn]     = useState("");
  const [interestOther, setInterestOther]   = useState("");
  const [actionRequired, setActionRequired] = useState("");
  const [nextDate, setNextDate]             = useState("");
  const [closedReason, setClosedReason]     = useState("");

  // Mark Missed form state
  const [missedReason, setMissedReason]     = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/followups/mine");
      setFollowUps(res.data);
    } catch { toast.error("Could not load follow-ups"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = tab === "all" ? followUps : followUps.filter(f => f.status === tab);

  const openDone = (f) => {
    setSelected(f);
    setModal("complete");
    setFeedback(""); setTemperature(""); setPipelineStage("");
    setInterestedIn(f.interested_in || "");
    setInterestOther("");
    setActionRequired(f.action_required || "");
    setNextDate(""); setClosedReason("");
  };
  const openMissed = (f) => {
    setSelected(f);
    setModal("missed");
    setMissedReason(""); setRescheduleDate("");
  };
  const closeModal = () => { setModal(null); setSelected(null); };

  const minDate = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  })();

  const isNotInterested = temperature === "not_interested";
  const interestOptions = user?.company === "ebekmed" ? EBEKMED_INTEREST_OPTIONS : INTEREST_OPTIONS;

  const handleComplete = async () => {
    if (!feedback.trim())    return toast.error("Please enter feedback from this visit");
    if (!temperature)        return toast.error("Please select the current lead temperature");
    if (isNotInterested && !closedReason.trim()) return toast.error("Please enter the reason for closing");

    setSubmitting(true);
    try {
      const body = {
        feedback,
        action_required:     actionRequired || null,
        interested_in:       interestedIn === "others" && interestOther.trim() ? interestOther.trim() : (interestedIn || null),
        lead_temperature:    temperature,
        pipeline_stage:      (!isNotInterested && pipelineStage) ? pipelineStage : null,
        next_follow_up_date: (!isNotInterested && nextDate)      ? nextDate      : null,
        closed_reason:       isNotInterested ? closedReason      : null,
        notes:               feedback,
      };
      const res = await API.put(`/followups/${selected.id}/complete`, body);
      celebrate("followup", { message: res.data.next_follow_up ? "Done—and the next follow-up is scheduled" : "Follow-up completed successfully" });
      celebrate("followup", { message: res.data.next_follow_up ? "Done—and the next follow-up is scheduled" : "Follow-up completed successfully" });

      if      (res.data.closed_lead)    toast.success("Lead marked as Not Interested  closed ");
      else if (res.data.next_follow_up) toast.success("Done! Next follow-up scheduled ");
      else                              toast.success("Follow-up completed ");

      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update");
    } finally { setSubmitting(false); }
  };

  const handleMissed = async () => {
    if (!missedReason.trim()) return toast.error("Please enter reason for missing this follow-up");
    setSubmitting(true);
    try {
      const res = await API.put(`/followups/${selected.id}/missed`, {
        missed_reason:       missedReason,
        next_follow_up_date: rescheduleDate || null,
      });
      toast.success(res.data.rescheduled ? "Marked missed & rescheduled " : "Marked as missed");
      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update");
    } finally { setSubmitting(false); }
  };

  const inputStyle = { width: "100%", padding: "12px 14px", borderRadius: 12, border: "1.5px solid #e2e8f0", fontSize: 13, fontFamily: "inherit", outline: "none", background: "#fff", boxSizing: "border-box" };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 6, display: "block", textTransform: "uppercase", letterSpacing: 0.5 };

  return (
    <div style={{ paddingBottom: 90 }}>
      {/*  Top Header with dual tabs  */}
      <div style={{ background: "#fff", borderBottom: "1px solid #f1f5f9", padding: "16px 20px 0" }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b", marginBottom: 4 }}>Follow-ups</div>
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>Track revisits & log contact actions</div>

        {/* Main tabs: Scheduled | Quick Actions */}
        <div style={{ display: "flex", gap: 0, borderRadius: 12, background: "#f1f5f9", padding: 4, marginBottom: 12 }}>
          <button onClick={() => setMainTab("followups")}
            style={{
              flex: 1, padding: "9px 8px", borderRadius: 10, border: "none",
              background: mainTab === "followups" ? "#fff" : "transparent",
              color: mainTab === "followups" ? accent : "#64748b",
              fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              boxShadow: mainTab === "followups" ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              transition: "all 0.2s"
            }}>
             Scheduled Follow-ups
            {followUps.filter(f => f.status === "scheduled").length > 0 && (
              <span style={{ marginLeft: 6, background: accent, color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10 }}>
                {followUps.filter(f => f.status === "scheduled").length}
              </span>
            )}
          </button>
          <button onClick={() => setMainTab("actions")}
            style={{
              flex: 1, padding: "9px 8px", borderRadius: 10, border: "none",
              background: mainTab === "actions" ? "#fff" : "transparent",
              color: mainTab === "actions" ? "#7c3aed" : "#64748b",
              fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              boxShadow: mainTab === "actions" ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              transition: "all 0.2s"
            }}>
             Quick Actions
          </button>
        </div>

        {/* Scheduled follow-ups sub-tabs */}
        {mainTab === "followups" && (
          <>
            {/* Stats pills */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12, overflowX: "auto", paddingBottom: 4 }}>
              {[
                { label: "Upcoming", count: followUps.filter(f => f.status === "scheduled" && !isOverdue(f)).length, color: "#7c3aed", bg: "#f5f3ff" },
                { label: "Overdue",  count: followUps.filter(f => isOverdue(f)).length,                              color: "#dc2626", bg: "#fef2f2" },
                { label: "Missed",   count: followUps.filter(f => f.status === "missed").length,                     color: "#ea580c", bg: "#fff7ed" },
                { label: "Done",     count: followUps.filter(f => f.status === "completed").length,                  color: "#16a34a", bg: "#f0fdf4" },
              ].map(s => (
                <div key={s.label} style={{ flexShrink: 0, padding: "6px 14px", background: s.bg, borderRadius: 20, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.count}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: s.color }}>{s.label}</span>
                </div>
              ))}
            </div>

            {/* Sub-tabs */}
            <div style={{ display: "flex", gap: 4 }}>
              {STATUS_TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  style={{ flex: 1, padding: "9px 4px", background: "none", border: "none", borderBottom: `2.5px solid ${tab === t.key ? accent : "transparent"}`, color: tab === t.key ? accent : "#94a3b8", fontWeight: 700, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/*  Panel content  */}
      {mainTab === "actions" ? (
        <QuickActionsPanel accent={accent} />
      ) : (
        <>
          {/* Scheduled Follow-ups List */}
          <div style={{ padding: "12px 16px" }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>Loading</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}></div>
                <div style={{ fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>No follow-ups here</div>
                <div style={{ fontSize: 13, color: "#94a3b8" }}>Schedule follow-ups when logging visits</div>
              </div>
            ) : filtered.map(f => {
              const overdue = isOverdue(f);
              const statusColor = overdue ? "#dc2626" : f.status === "completed" ? "#16a34a" : f.status === "missed" ? "#ea580c" : "#7c3aed";
              const statusBg    = overdue ? "#fef2f2" : f.status === "completed" ? "#f0fdf4" : f.status === "missed" ? "#fff7ed" : "#f5f3ff";
              const statusLabel = overdue ? "Overdue" : f.status.charAt(0).toUpperCase() + f.status.slice(1);

              return (
                <div key={f.id} style={{ background: "#fff", borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${overdue ? "#fecaca" : "#e2e8f0"}`, boxShadow: overdue ? "0 0 0 2px #fecaca22" : "none" }}>
                  {/* Top row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: "#1e293b", fontSize: 15, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.institution_name}</div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>Scheduled: {formatDate(f.scheduled_date)}</div>
                    </div>
                    <div style={{ padding: "4px 12px", background: statusBg, borderRadius: 20, color: statusColor, fontWeight: 700, fontSize: 11, flexShrink: 0, marginLeft: 10 }}>
                      {statusLabel}
                    </div>
                  </div>

                  {/* Pipeline stage badge */}
                  {f.lead_id && f.pipeline_stage && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ padding: "3px 10px", background: (STAGE_COLORS[f.pipeline_stage] || "#94a3b8") + "20", color: STAGE_COLORS[f.pipeline_stage] || "#94a3b8", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>
                         {STAGE_LABELS[f.pipeline_stage] || f.pipeline_stage}
                      </span>
                    </div>
                  )}

                  {/* Tags */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {f.interested_in && (
                      <span style={{ padding: "3px 10px", background: "#eff6ff", color: "#1d4ed8", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                         {INTEREST_LABELS[f.interested_in] || f.interested_in}
                      </span>
                    )}
                    {f.action_required && (
                      <span style={{ padding: "3px 10px", background: "#f5f3ff", color: "#7c3aed", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                         {ACTION_LABELS[f.action_required] || f.action_required}
                      </span>
                    )}
                  </div>

                  {/* Completed feedback */}
                  {f.status === "completed" && f.feedback && (
                    <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: "#15803d" }}>
                      <strong>Feedback:</strong> {f.feedback}
                    </div>
                  )}

                  {/* Missed reason */}
                  {f.status === "missed" && f.missed_reason && (
                    <div style={{ background: "#fff7ed", borderRadius: 10, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: "#9a3412" }}>
                      <strong>Missed reason:</strong> {f.missed_reason}
                    </div>
                  )}

                  {/* Calendar + actions */}
                  {(f.status === "scheduled" || overdue) && (
                    <>
                      {/* Add to Calendar row */}
                      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                        <a href={`${process.env.REACT_APP_API_URL || "http://localhost:4001"}/api/followups/${f.id}/ics`}
                          target="_blank" rel="noreferrer"
                          style={{ flex: 1, padding: "7px 0", background: "#eff6ff", color: "#1d4ed8", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 11, cursor: "pointer", textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                          📅 Save .ics
                        </a>
                        <button onClick={async () => {
                          try {
                            const API = (await import("../../utils/api")).default;
                            const r = await API.get(`/followups/${f.id}/gcal-link`);
                            window.open(r.data.url, "_blank");
                          } catch { }
                        }}
                          style={{ flex: 1, padding: "7px 0", background: "#fff7ed", color: "#c2410c", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 11, cursor: "pointer" }}>
                          📆 Google Cal
                        </button>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => openDone(f)}
                          style={{ flex: 1, padding: "10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                           Mark Done
                        </button>
                        <button onClick={() => openMissed(f)}
                          style={{ flex: 1, padding: "10px", background: "#fff", color: "#dc2626", border: "1.5px solid #fecaca", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                           Missed
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/*  MARK DONE MODAL  */}
          {modal === "complete" && selected && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}>
              <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 17, color: "#1e293b" }}> Log Visit Outcome</div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>{selected.institution_name}</div>
                  </div>
                  <button onClick={closeModal}
                    style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}></button>
                </div>

                {/* Feedback */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Visit Feedback *</label>
                  <textarea value={feedback} onChange={e => setFeedback(e.target.value)}
                    placeholder="What happened during this visit? Outcome, discussions"
                    style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} />
                </div>

                {/* Lead Temperature */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Current Lead Status * <span style={{ color: "#94a3b8", fontSize: 10 }}> select one</span></label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {TEMPERATURE_OPTIONS.map(opt => {
                      const sel = temperature === opt.value;
                      return (
                        <button key={opt.value} onClick={() => {
                          setTemperature(sel ? "" : opt.value);
                          if (opt.value !== "not_interested") setClosedReason("");
                          if (opt.value === "not_interested") { setNextDate(""); setPipelineStage(""); }
                        }}
                          style={{ padding: "9px 16px", borderRadius: 20, border: `2px solid ${sel ? opt.bg === "#1e293b" ? "#1e293b" : opt.color : "#e2e8f0"}`,
                            background: sel ? opt.bg : "#f8fafc",
                            color: sel ? opt.color : "#475569",
                            fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                            boxShadow: sel ? `0 0 0 3px ${opt.bg === "#1e293b" ? "#1e293b30" : opt.color + "25"}` : "none" }}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Not Interested */}
                {isNotInterested && (
                  <div style={{ marginBottom: 16, background: "#1e293b10", borderRadius: 12, padding: 14, border: "1.5px solid #1e293b30" }}>
                    <label style={{ ...labelStyle, color: "#1e293b" }}>Reason for Closing *</label>
                    <textarea value={closedReason} onChange={e => setClosedReason(e.target.value)}
                      placeholder="e.g. Budget constraints, competition, not relevant product"
                      style={{ ...inputStyle, resize: "vertical", minHeight: 70, borderColor: "#1e293b30" }} />
                    <div style={{ marginTop: 8, fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                       This lead will be <strong>closed and hidden</strong> from your active list.
                    </div>
                  </div>
                )}

                {!isNotInterested && (
                  <>
                    {/* Interested In */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>Interested In (optional)</label>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {interestOptions.map(opt => {
                          const sel = interestedIn === opt.value;
                          return (
                            <button key={opt.value} onClick={() => setInterestedIn(sel ? "" : opt.value)}
                              style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${sel ? accent : "#e2e8f0"}`,
                                background: sel ? accent : "#f8fafc", color: sel ? "#fff" : "#475569",
                                fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      {interestedIn === "others" && (
                        <input value={interestOther} onChange={e => setInterestOther(e.target.value)}
                          placeholder="Type product / service name"
                          style={{ ...inputStyle, marginTop: 8 }} />
                      )}
                    </div>

                    {/* Action Required */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>Next Action Required (optional)</label>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {ACTION_OPTIONS.map(opt => {
                          const sel = actionRequired === opt.value;
                          return (
                            <button key={opt.value} onClick={() => setActionRequired(sel ? "" : opt.value)}
                              style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${sel ? "#7c3aed" : "#e2e8f0"}`,
                                background: sel ? "#7c3aed" : "#f8fafc", color: sel ? "#fff" : "#475569",
                                fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Advance Pipeline Stage */}
                    {selected.lead_id && (
                      <div style={{ marginBottom: 14, background: "#f0fdf4", borderRadius: 12, padding: 14, border: "1.5px solid #86efac" }}>
                        <label style={{ ...labelStyle, color: "#15803d" }}> Advance Pipeline Stage? (optional)</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {PIPELINE_ADVANCE_OPTIONS.map(opt => {
                            const sel = pipelineStage === opt.value;
                            return (
                              <button key={opt.value} onClick={() => setPipelineStage(sel ? "" : opt.value)}
                                style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${sel ? "#16a34a" : "#e2e8f0"}`,
                                  background: sel ? "#dcfce7" : "#fff", color: sel ? "#16a34a" : "#64748b",
                                  fontWeight: 600, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Next Follow-up Date */}
                    <div style={{ marginBottom: 20, background: "#faf5ff", borderRadius: 12, padding: 14, border: "1.5px solid #e9d5ff" }}>
                      <label style={{ ...labelStyle, color: "#7c3aed" }}>Schedule Next Follow-up (optional)</label>
                      <input type="date" min={minDate} value={nextDate}
                        onChange={e => setNextDate(e.target.value)}
                        style={{ ...inputStyle, borderColor: "#e9d5ff" }} />
                      {nextDate && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#7c3aed", fontWeight: 600 }}>
                           Next follow-up: {formatDate(nextDate)}  WhatsApp reminder sent 1 day before
                        </div>
                      )}
                    </div>
                  </>
                )}

                <button onClick={handleComplete} disabled={submitting}
                  style={{ width: "100%", padding: 15,
                    background: isNotInterested ? "#1e293b" : "#16a34a",
                    color: "#fff", border: "none", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {submitting ? "Saving" : isNotInterested ? " Close Lead as Not Interested" : " Submit Outcome"}
                </button>
              </div>
            </div>
          )}

          {/*  MARK MISSED MODAL  */}
          {modal === "missed" && selected && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}>
              <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxHeight: "80vh", overflowY: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 17, color: "#1e293b" }}> Mark as Missed</div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>{selected.institution_name}</div>
                  </div>
                  <button onClick={closeModal}
                    style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}></button>
                </div>

                <div style={{ background: "#fff7ed", borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13, color: "#9a3412" }}>
                   Please explain why this follow-up was missed. You can reschedule it below.
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Reason for Missing *</label>
                  <textarea value={missedReason} onChange={e => setMissedReason(e.target.value)}
                    placeholder="e.g. Institution was closed, contact unavailable, emergency"
                    style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} />
                </div>

                <div style={{ marginBottom: 20, background: "#faf5ff", borderRadius: 12, padding: 14, border: "1.5px solid #e9d5ff" }}>
                  <label style={{ ...labelStyle, color: "#7c3aed" }}>Reschedule Follow-up (optional)</label>
                  <input type="date" min={minDate} value={rescheduleDate}
                    onChange={e => setRescheduleDate(e.target.value)}
                    style={{ ...inputStyle, borderColor: "#e9d5ff" }} />
                  {rescheduleDate && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#7c3aed", fontWeight: 600 }}>
                       Rescheduled to: {formatDate(rescheduleDate)}
                    </div>
                  )}
                </div>

                <button onClick={handleMissed} disabled={submitting}
                  style={{ width: "100%", padding: 15, background: "#dc2626", color: "#fff", border: "none", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {submitting ? "Saving" : rescheduleDate ? " Mark Missed & Reschedule" : " Mark as Missed"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
